import {
  List,
  ListDisplayType,
  Page,
  Tile as TileModel,
} from "@blacknut/javascript-sdk/dist";
import {
  Focusable,
  FocusableSection,
  FocusableSectionProps,
  Config,
} from "@blacknut/spatialnav-sdk/dist";
import clsx from "clsx";
import { AppLayout, useLayout } from "core";
import React, { FocusEvent } from "react";
import { useMoreLink } from "src/utils/useMoreLink";
import { ReactComponent as Arrow } from "../../../assets/dist/ic_arrow.svg";
import dimens from "../../../theme/dimens";
import { tile2Id } from "../../../utils/Utils";
import Tile from "../../Tile/Tile";
import ArrowButton from "../ArrowButton";
import globalListStyles from "../styles.module.scss";
import styles from "./styles.module.scss";
import { useTileSize } from "src/utils/TileWidthProvider";

export declare type KeyProviderFn = (item: TileModel, index: number) => string;
export declare type RenderTileFn = (item: TileModel) => JSX.Element;

export interface HorizontalTileListProps
  extends Pick<FocusableSectionProps, "leaveFor" | "disabled"> {
  list: List;
  showTitle?: boolean;
  inView?: boolean;
  className?: string;
  arrowClassName?: string;
  sectionTitleColor?: string;
  onFocus?: (e: FocusEvent<HTMLDivElement>) => void;
  isBranded?: boolean;
  scrollable?: boolean;
  renderTile?: RenderTileFn;
  keyProvider?: KeyProviderFn;
  page?: Page;
  testId?: string;
}

const Items = ({
  list,
  items,
  inView,
  renderTile,
  keyProvider,
  page,
}: {
  list: List;
  items: TileModel[];
  inView: boolean;
  renderTile?: RenderTileFn;
  keyProvider?: KeyProviderFn;
  page?: Page;
}) => {
  const { standardTileSize, featured16By9TileSize } = useTileSize();

  return (
    <ul className={styles.list}>
      {items.map((tile, index) => {
        return (
          <li
            key={
              keyProvider ? keyProvider(list, index) : `${list.uuid}-${tile2Id(tile)}`
            }
            style={{
              minWidth:
                list.display === ListDisplayType.FEATURED
                  ? featured16By9TileSize.width
                  : standardTileSize.width,
              minHeight:
                list.display === ListDisplayType.FEATURED
                  ? featured16By9TileSize.height
                  : standardTileSize.height,
            }}
          >
            {(inView || index === 0) &&
              (renderTile ? (
                renderTile(tile)
              ) : (
                <Tile item={tile} list={list} page={page} />
              ))}
          </li>
        );
      })}
    </ul>
  );
};

const SPATIAL_CONFIG: Config = { restrict: "self-first", distanceMode: true };
const HorizontalTileListNew = ({
  list,
  showTitle = true,
  className,
  arrowClassName,
  sectionTitleColor,
  leaveFor,
  disabled,
  inView = true,
  onFocus,
  isBranded = false,
  scrollable = false,
  renderTile,
  keyProvider,
  page,
  testId,
}: HorizontalTileListProps) => {
  const layout = useLayout();
  const { handleMoreLink } = useMoreLink(list.moreLink);
  const hasTitle = showTitle && list.title && list.title.trim().length > 0;
  return (
    <FocusableSection
      className={clsx(className, styles.container)}
      focusKey={list.uuid}
      leaveFor={leaveFor}
      disabled={disabled}
      enterTo="default-element"
      defaultElement={`.tile`}
      config={SPATIAL_CONFIG}
      onFocus={onFocus}
      onMouseDown={(e) => e.preventDefault()}
    >
      {!isBranded && (hasTitle || (!!list.moreLink && layout === AppLayout.PHONE)) && (
        <div className={clsx(styles.titleContainer, scrollable && styles.scrollable)}>
          {/* FIXME Extra margin if no title */}
          {hasTitle && (
            <Focusable
              className={clsx(styles.title, !!list.moreLink && styles.titleClickable)}
              style={{ color: sectionTitleColor }}
              onClick={handleMoreLink}
              onPressEnter={handleMoreLink}
              disabled={
                !list.moreLink || window.innerWidth >= dimens.breakpoints.desktop
              }
            >
              {list.title}
              {process.env.NODE_ENV === "development" && `(${list.display})`}
              <Arrow />
            </Focusable>
          )}
        </div>
      )}

      <div
        className={clsx(styles.listContainer, scrollable && styles.scrollable)}
        data-testid={testId ? `${testId}-list` : undefined}
      >
        <Items
          list={list}
          items={list.tiles}
          inView={inView}
          renderTile={renderTile}
          keyProvider={keyProvider}
          page={page}
        />

        {list.moreLink && (
          <ArrowButton
            onPress={handleMoreLink}
            className={clsx(globalListStyles.arrowButton, arrowClassName, styles.arrow)}
          />
        )}
      </div>
    </FocusableSection>
  );
};

export default HorizontalTileListNew;
